/* Build-the-Kit
   Custom element <kit-picker>. Manages tier selection, total recalc on variant change,
   and multi-line ATC posting to /cart/add.js with items: [...].
   Subscribes to PUB_SUB_EVENTS.variantChange so kit totals stay in sync with PDP variant.
   Uses bare globals: PUB_SUB_EVENTS, subscribe (declared as const + function in global.js).
*/

if (!customElements.get('kit-picker')) {
  class KitPicker extends HTMLElement {
    constructor() {
      super();
      this.config = {};
      this.tiers = [];
      this.selectedTier = null;
      this.submitButton = null;
      this.submitLabel = null;
      this.errorEl = null;
      this.variantSubscription = null;
      this.moneyFormat = '${{amount}}';
      this.initialVariantPrice = 0;
      this.currentVariantId = null;
      this.currentVariantPrice = 0;
      this.inFlight = false;
    }

    connectedCallback() {
      const cfgScript = this.querySelector('script[data-kit-config]');
      if (cfgScript) {
        try { this.config = JSON.parse(cfgScript.textContent || '{}'); }
        catch (e) { this.config = {}; }
      }
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.initialVariantPrice = parseInt(this.config.initialVariantPrice, 10) || 0;
      this.currentVariantId = this.config.initialVariantId || null;
      this.currentVariantPrice = this.initialVariantPrice;

      this.tiers = Array.from(this.querySelectorAll('.kit-tier'));
      this.submitButton = this.querySelector('[data-kit-submit]');
      this.submitLabel = this.querySelector('[data-kit-submit-label]');
      this.errorEl = this.querySelector('[data-kit-error]');
      this.stockMsgEl = this.querySelector('[data-kit-stock-msg]');

      // Collar size picker state. The Complete Bundle (tier 3) carries a 3-pill
      // collar size selector. Customer must pick the size explicitly because
      // dog neck size does not correlate to harness body size. Logan's call.
      this._collarSelectedSize = null;
      this._collarVariantsBySize = {}; // { size_value: variant_id } for the currently-matched color

      // Tag the host product-info so CSS that can't rely on :has() (Safari < 15.4)
      // can still suppress the duplicate ATC + qty selector via a class fallback.
      const productInfo = this.closest('product-info');
      if (productInfo) productInfo.classList.add('has-kit-picker');

      this.tiers.forEach(tier => {
        const radio = tier.querySelector('.kit-tier__radio');
        if (radio) {
          radio.addEventListener('change', () => this.selectTier(tier));
        }
      });

      // Bind collar size pill click handlers (delegated within the picker so a
      // single bind covers all pills regardless of how many sizes are rendered).
      // Fix 8: stored as instance property so disconnectedCallback can remove it.
      this._collarPillHandler = (evt) => {
        const pill = evt.target.closest('[data-kit-collar-size-option]');
        if (!pill) return;
        evt.preventDefault();
        this.onCollarSizeClick(pill);
      };
      this.addEventListener('click', this._collarPillHandler);

      // Fix 5: roving-tabindex keyboard navigation for the collar radiogroup.
      this._collarRadiogroupHandler = (evt) => {
        const group = evt.target.closest('[data-kit-collar-size]');
        if (!group) return;
        const radios = Array.from(group.querySelectorAll('[role="radio"]:not([aria-disabled="true"])'));
        if (radios.length === 0) return;
        const allRadios = Array.from(group.querySelectorAll('[role="radio"]'));
        const current = allRadios.find((r) => r.getAttribute('aria-checked') === 'true') || radios[0];
        const currentIdx = radios.indexOf(current);

        let target = null;
        if (evt.key === 'ArrowRight' || evt.key === 'ArrowDown') {
          evt.preventDefault();
          target = radios[(currentIdx + 1) % radios.length];
        } else if (evt.key === 'ArrowLeft' || evt.key === 'ArrowUp') {
          evt.preventDefault();
          target = radios[(currentIdx - 1 + radios.length) % radios.length];
        } else if (evt.key === 'Home') {
          evt.preventDefault();
          target = radios[0];
        } else if (evt.key === 'End') {
          evt.preventDefault();
          target = radios[radios.length - 1];
        } else if (evt.key === ' ' || evt.key === 'Enter') {
          evt.preventDefault();
          const focused = group.querySelector('[role="radio"]:focus');
          if (focused && focused.getAttribute('aria-disabled') !== 'true') this.onCollarSizeClick(focused);
          return;
        }

        if (target) {
          // Move roving tabindex: focused radio gets 0, rest get -1.
          allRadios.forEach((r) => r.setAttribute('tabindex', r === target ? '0' : '-1'));
          target.focus();
          // Selecting on arrow move matches standard radiogroup pattern.
          if (target.getAttribute('aria-disabled') !== 'true') this.onCollarSizeClick(target);
        }
      };
      this.addEventListener('keydown', this._collarRadiogroupHandler);

      // Custom Tuff Pupper Size Guide bottom-sheet. The modal markup lives
      // next to <kit-picker> in build-the-kit.liquid. Find the nearest one
      // (same DOM scope as this picker) once, then bind open/close.
      this._sizeGuide =
        this.parentElement?.querySelector('[data-kit-size-guide]') ||
        document.querySelector('[data-kit-size-guide]');

      if (this._sizeGuide) {
        // The sheet is position:fixed but lives under .product__info-container,
        // which carries a transform. A transformed ancestor becomes the
        // containing block for fixed descendants, so inset:0 sizes to that
        // container (taller than the viewport) instead of the screen, pushing
        // the flex-end sheet below the fold on mobile. Relocate it to <body>
        // once so position:fixed is viewport-relative again.
        if (this._sizeGuide.parentElement !== document.body) {
          document.body.appendChild(this._sizeGuide);
        }
        // Fix 1: focus trap for WCAG 2.4.3. Cycles Tab/Shift+Tab within the sheet.
        // Registered on the sheet element when it opens, removed when it closes.
        // We do NOT use the `inert` attribute (it would propagate to descendants
        // and conflict with the existing position:fixed scroll-lock). We also skip
        // aria-hidden on sibling containers because we cannot safely identify a
        // single main-content sibling without risking hiding the sheet itself.
        this._sizeGuideFocusTrap = (e) => {
          if (e.key !== 'Tab') return;
          const focusable = Array.from(
            this._sizeGuide.querySelectorAll(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
          ).filter((el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true');
          if (focusable.length === 0) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (e.shiftKey) {
            if (document.activeElement === first) { e.preventDefault(); last.focus(); }
          } else {
            if (document.activeElement === last) { e.preventDefault(); first.focus(); }
          }
        };

        const closeGuide = () => {
          if (!this._sizeGuide) return;
          this._sizeGuide.removeAttribute('data-open');
          this._sizeGuide.setAttribute('aria-hidden', 'true');
          // Remove focus trap when sheet closes.
          this._sizeGuide.removeEventListener('keydown', this._sizeGuideFocusTrap);
          this._unlockBodyScroll();
          // Restore focus to the trigger that opened the sheet.
          if (this._sizeGuideTrigger && typeof this._sizeGuideTrigger.focus === 'function') {
            this._sizeGuideTrigger.focus();
          }
          // Hide attr after the slide-out so the element leaves the a11y tree.
          // Pointer-events are already off via CSS gate on [data-open], so the
          // 340ms window is now safe even if this setTimeout never fires.
          setTimeout(() => {
            if (this._sizeGuide && !this._sizeGuide.hasAttribute('data-open')) this._sizeGuide.hidden = true;
          }, 340);
        };

        // Document-level event delegation for close. Survives any subtree
        // re-render of the modal (third-party apps mutating the DOM, section
        // renders, etc.) where per-element listeners would silently detach
        // and leave the customer with no way to close the sheet.
        this._sizeGuideDocClick = (evt) => {
          if (!this._sizeGuide?.hasAttribute('data-open')) return;
          if (evt.target.closest('[data-kit-size-guide-close]')) closeGuide();
        };
        document.addEventListener('click', this._sizeGuideDocClick);

        this._sizeGuideKeydown = (e) => {
          if (e.key === 'Escape' && this._sizeGuide?.hasAttribute('data-open')) closeGuide();
        };
        document.addEventListener('keydown', this._sizeGuideKeydown);

        // Safety net: if the page becomes visible again (tab switch, bfcache
        // restore, return-from-cart navigation) and we still hold a lock
        // with no open size-guide in the DOM, the close path failed at some
        // point. Auto-restore so the customer is never permanently stuck.
        // Gated on this._lockHeld so a third-party script's own
        // position:fixed on body never triggers our scrollTo.
        this._sizeGuideRecover = () => {
          if (!this._lockHeld) return;
          // Fix 2: only run when the tab is actually becoming visible AND the guide
          // is not legitimately open. Without this check a tab-switch while the
          // sheet is open (visibilityState='hidden') would fire, unlock body scroll,
          // and leave the sheet open but unscrollable.
          if (document.visibilityState !== 'visible') return;
          const hasOpenGuide = document.querySelector('[data-kit-size-guide][data-open]');
          if (!hasOpenGuide) this._unlockBodyScroll();
        };
        document.addEventListener('visibilitychange', this._sizeGuideRecover);
        window.addEventListener('pagehide', this._sizeGuideRecover);
        window.addEventListener('pageshow', this._sizeGuideRecover);

        this._closeSizeGuide = closeGuide;
      }

      // Inline "Size guide" trigger inside the Collar fit row. Opens the
      // on-brand bottom sheet instead of opening a new tab or piggy-backing
      // on Ignite's generic side modal. Capture phase so the click never
      // reaches the parent label and re-fires the tier radio.
      this._sizeGuideLinkHandler = (evt) => {
        const trigger = evt.target.closest('.kit-tier__size-guide-link');
        if (!trigger) return;
        evt.preventDefault();
        evt.stopPropagation();
        if (this._sizeGuide) {
          // Fix 3: if a lock is already held the sheet is already open; opening
          // a second time would re-read scrollY=0 (body is position:fixed) and
          // corrupt the restore position on close.
          if (this._lockHeld) return;
          this._sizeGuideTrigger = trigger;
          // Scroll-lock without CLS. Three things matter:
          //   1. body.overflow:hidden alone causes iOS Safari to scroll
          //      to top, so we pin body via position:fixed instead.
          //   2. position:fixed without top:-scrollY would jump to top, so
          //      we record current scrollY and offset the body upward by
          //      the same amount. From the user's view, nothing moves.
          //   3. Desktop scrollbar disappear shifts content horizontally,
          //      so we pad-right by the scrollbar width to keep layout
          //      stable. Mobile overlay scrollbars have width 0, no-op.
          this._lockedScrollY = window.scrollY;
          const sbWidth = window.innerWidth - document.documentElement.clientWidth;
          if (sbWidth > 0) document.body.style.paddingRight = sbWidth + 'px';
          document.body.style.position = 'fixed';
          document.body.style.top = `-${this._lockedScrollY}px`;
          document.body.style.left = '0';
          document.body.style.right = '0';
          document.body.style.width = '100%';
          // Mark the lock as ours so the visibility/pageshow watchdog can
          // distinguish our scroll lock from any third-party position:fixed
          // on body and skip the auto-recovery scrollTo in that case.
          this._lockHeld = true;
          this._sizeGuide.hidden = false;
          this._sizeGuide.setAttribute('aria-hidden', 'false');
          // Force one frame so the [hidden] -> visible transition can run.
          requestAnimationFrame(() => {
            this._sizeGuide.setAttribute('data-open', '');
            // Fix 1: register the focus trap after the sheet is visually open.
            this._sizeGuide.addEventListener('keydown', this._sizeGuideFocusTrap);
            this._sizeGuide.querySelector('.kit-size-guide__close')?.focus();
          });
        } else {
          window.open('/pages/harness-size-guide', '_blank', 'noopener');
        }
      };
      // Fix 8: store handler as instance property so disconnectedCallback can remove it.
      this.addEventListener('click', this._sizeGuideLinkHandler, true);
      const initial = this.querySelector('.kit-tier--selected') || this.tiers[0];
      if (initial) this.selectTier(initial, { silent: true });
      // Mark initialized AFTER first selectTier so the SR status doesn't fire on page load.
      this._initialized = true;

      if (this.submitButton) {
        this.submitButton.addEventListener('click', this.onSubmit.bind(this));
        // Disable on init if the main variant is unavailable (fix #5).
        if (this.dataset.initialAvailable === 'false') {
          this.submitButton.setAttribute('aria-disabled', 'true');
        }
      }

      // Subscribe via the bare globals declared in global.js (NOT on window).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantSubscription = subscribe(
          PUB_SUB_EVENTS.variantChange,
          (event) => this.onVariantChange(event)
        );
      }

      // Trigger first-render animations on next frame (staggered tier rise + thumb fade + popular-badge drop).
      // Skipped automatically under prefers-reduced-motion via the CSS guard.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.add('kit-picker--ready'));
      });

      // Initialise the kit-aware Shop Pay interceptor: on bundle tiers, click on the
      // native Shop Pay button is rerouted to a cart permalink that carries every
      // bundle item, then triggers Shop Pay express via ?payment=shop_pay. On Tier 1
      // (single variant) we leave the native button alone for true zero-leave express.
      this.syncShopPayKit();

      // Match addons to the harness's initial color on page load. Without this, the
      // Liquid-rendered defaults (first_available_variant of each addon) may not match
      // the harness's selected color until the customer triggers a variant change.
      if (this.config.initialColor) {
        this.syncAddonsToColor({
          id: this.currentVariantId,
          option1: this.config.initialColor,
          price: this.initialVariantPrice,
          featured_image: null
        });
      }

      // Show the OOS stock message on first paint if the harness is unavailable in the
      // initial variant. Use the colour + size from data-kit-config so the message reads
      // like "Epic Black / Small is sold out." rather than a generic "Out of stock".
      if (this.config.initialAvailable === false) {
        this.applyStockState({
          available: false,
          option1: this.config.initialColor || '',
          option2: this.config.initialSize || ''
        });
      }
    }

    fetchAddonProduct(handle) {
      this._addonCache = this._addonCache || {};
      if (this._addonCache[handle]) return this._addonCache[handle];
      // Don't poison the cache on a transient failure. Cache the in-flight
      // Promise so concurrent callers share one network round-trip, but if
      // it resolves to null (network blip, 5xx, 404) drop the cache entry
      // so the next call retries instead of returning the stale null
      // forever. Without this guard a single failed first-fetch silently
      // disabled addon syncing for the rest of the session.
      const promise = fetch(`/products/${handle}.js`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
        .then((result) => {
          // Guard against disconnectedCallback nulling _addonCache while
          // this fetch was in flight. delete on null[key] throws TypeError
          // and would crash the upstream Promise.all consumer.
          if (result == null && this._addonCache) delete this._addonCache[handle];
          return result;
        });
      this._addonCache[handle] = promise;
      return promise;
    }

    matchAddonVariantByColor(addonProduct, color) {
      if (!addonProduct || !addonProduct.variants || !color) return null;
      const want = String(color).toLowerCase().trim();
      const wantWords = want.split(/\s+/).filter(Boolean);
      const variants = addonProduct.variants.filter((v) => v.available !== false);
      // Try exact match first.
      let m = variants.find((v) => String(v.option1 || '').toLowerCase().trim() === want);
      if (m) return m;
      // Fuzzy: any shared word (e.g. "Hot Pink" vs "Punch Pink" -> "pink"; "March Red" vs "Crimson Red" -> "red").
      m = variants.find((v) => {
        const have = String(v.option1 || '').toLowerCase().split(/\s+/);
        return wantWords.some((w) => w.length > 2 && have.includes(w));
      });
      return m || null;
    }

    syncAddonsToColor(harnessVariant) {
      const config = this.config || {};
      const addonHandlesByTier = config.addonHandles || {};
      const harnessColor = harnessVariant.option1; // Tracker / Metro: option1 is Color.

      // Token-stamp every call. Two rapid color flips kick off two async fetches; if
      // the older one resolves second (slow CDN, race) it must NOT overwrite the newer
      // tierAddons / thumbnails. Only the latest sync's resolution applies.
      this._syncToken = (this._syncToken || 0) + 1;
      const myToken = this._syncToken;

      this.tiers.forEach((tier) => {
        const tierKey = `tier_${tier.dataset.tier}`;
        const handles = addonHandlesByTier[tierKey] || [];
        if (handles.length === 0) return;

        const thumbEls = [
          tier.querySelector('[data-kit-thumb-role="addon-0"] img'),
          tier.querySelector('[data-kit-thumb-role="addon-1"] img')
        ];

        Promise.all(handles.map((h) => this.fetchAddonProduct(h))).then((products) => {
          if (myToken !== this._syncToken) return; // stale resolution, drop.
          // Match each addon to a variant in the harness color (or fall back to the
          // first available variant when no color match exists). Track which addons
          // are "exact" matches vs fallbacks so the thumbnail can flag the mismatch
          // (avoids a Hot Pink harness + Crimson Red leash visual mismatch).
          const matched = products.map((p, i) => {
            if (!p) return { variant: null, isFallback: true };
            const exact = this.matchAddonVariantByColor(p, harnessColor);
            if (exact) return { variant: exact, isFallback: false };
            const fb = (p.variants && p.variants.find((v) => v.available !== false)) || null;
            return { variant: fb, isFallback: true };
          });

          // Update thumbnails. On fallback (no color match), mark the thumb so CSS
          // can apply a desaturated treatment + the kit body shows a "best match"
          // microcopy instead of pretending the color matches.
          matched.forEach((m, i) => {
            const img = thumbEls[i];
            if (!img || !m.variant) return;
            const src = (m.variant.featured_image && m.variant.featured_image.src)
                     || (products[i] && products[i].featured_image)
                     || null;
            if (src) {
              img.src = src.indexOf('cdn.shopify.com') >= 0
                ? src.split('?')[0] + '?width=80'
                : src;
            }
            const thumbWrap = img.closest('.kit-tier__thumb');
            if (thumbWrap) thumbWrap.classList.toggle('kit-tier__thumb--fallback', m.isFallback);
          });

          // Recompute the tier's add-on variant id list + base total.
          const addonIds = matched.map((m) => m.variant && m.variant.id).filter(Boolean);
          tier.dataset.tierAddons = addonIds.join(',');
          // Capture the Liquid-rendered + color-matched defaults so selectTier
          // can revert if the customer leaves Tier 3 after picking a collar
          // size that overrode the defaults.
          tier._defaultTierAddons = addonIds.join(',');

          // For Tier 3, build the collar size → variant map for the currently
          // matched color and refresh the size picker availability state.
          if (tier.dataset.tier === '3') {
            const collarProduct = products[products.length - 1];
            this.syncCollarSizeOptionsForTier(tier, collarProduct, harnessColor);
          }

          // Compute the addon-only sum for THIS color match, then derive total.
          // We deliberately do NOT mutate tier.dataset.tierBaseTotal. That
          // attribute must remain the Liquid-rendered immutable initial value
          // so the delta formula in onVariantChange always anchors to a known
          // baseline. Mutating it caused a double-count where a follow-up
          // variant change read a post-color-flip base and re-added the
          // harness delta on top.
          let addonPrice = 0;
          matched.forEach((m) => { if (m.variant) addonPrice += m.variant.price; });
          const total = harnessVariant.price + addonPrice;
          const tierSavings = parseInt(tier.dataset.tierSavingsAmount, 10) || 0;
          const tierDiscounted = tierSavings > 0 ? Math.max(0, total - tierSavings) : total;
          tier.dataset.tierAddonPrice = String(addonPrice);
          tier.dataset.tierLiveTotal = String(total);
          tier.dataset.tierLiveDiscounted = String(tierDiscounted);

          // Refresh the displayed price + free-ship pill on this tier. The main
          // price element shows the discounted total; the strikethrough was-price
          // shows the pre-discount sum so the customer sees the visual savings.
          const priceEl = tier.querySelector('[data-kit-price]');
          if (priceEl) priceEl.textContent = this.formatMoney(tierDiscounted);
          const wasEl = tier.querySelector('[data-kit-price-was]');
          if (wasEl) wasEl.textContent = this.formatMoney(total);
          const ship = tier.querySelector('[data-kit-shipfree]');
          const threshold = parseInt(this.dataset.freeShipThreshold, 10) || 3500;
          if (ship) ship.classList.toggle('kit-tier__shipfree--hidden', tierDiscounted < threshold);

          // If this tier is currently selected, refresh the submit label + Shop Pay URL.
          if (tier === this.selectedTier) {
            this.refreshSubmitLabel(false);
            this.syncShopPayKit();
          }
        });
      });
    }

    syncCollarSizeOptionsForTier(tier, collarProduct, harnessColor) {
      // Build a { size: variantId } map for the collar variants whose color
      // best matches the harness color. The color is matched the same way as
      // matchAddonVariantByColor (exact, then fuzzy word match). If no color
      // matches at all, fall back to the largest available pool (any color)
      // so the customer can still pick a size.
      const sizePicker = tier.querySelector('[data-kit-collar-size]');
      if (!sizePicker || !collarProduct || !collarProduct.variants) return;
      const want = String(harnessColor || '').toLowerCase().trim();
      const wantWords = want.split(/\s+/).filter(Boolean);

      // Step 1: try exact color match.
      let candidates = collarProduct.variants.filter(
        (v) => v.available !== false && String(v.option1 || '').toLowerCase().trim() === want
      );
      // Step 2: fuzzy word match (e.g. "Hot Pink" harness -> "Punch Pink" collar).
      if (candidates.length === 0) {
        candidates = collarProduct.variants.filter((v) => {
          if (v.available === false) return false;
          const have = String(v.option1 || '').toLowerCase().split(/\s+/);
          return wantWords.some((w) => w.length > 2 && have.includes(w));
        });
      }
      // Step 3: fall back to any available variant so the customer can still pick.
      if (candidates.length === 0) {
        candidates = collarProduct.variants.filter((v) => v.available !== false);
      }

      // Build size -> variant id (smallest stock count tiebreaker not needed
      // since each size+color has a single variant in Shopify's schema).
      const sizeToVariant = {};
      candidates.forEach((v) => {
        const size = String(v.option2 || '').trim();
        if (!size) return;
        sizeToVariant[size] = v.id;
      });
      this._collarVariantsBySize = sizeToVariant;

      // Update each pill: enable if its size has a variant in this color map,
      // disable otherwise. Preserve the customer's selection if still valid.
      const pills = sizePicker.querySelectorAll('[data-kit-collar-size-option]');
      pills.forEach((pill) => {
        const size = pill.getAttribute('data-kit-collar-size-option');
        const hasVariant = !!sizeToVariant[size];
        if (hasVariant) {
          pill.removeAttribute('aria-disabled');
          // Fix 4: use aria-disabled only, never the real disabled attribute.
          // Real disabled removes the element from the tab order entirely.
          // aria-disabled keeps it focusable so keyboard users can encounter
          // and understand unavailable options (WCAG 2.1.1 / 4.1.2).
          pill.removeAttribute('disabled');
          // Ensure roving tabindex is reset when pill becomes available.
          if (pill.getAttribute('tabindex') === null) pill.setAttribute('tabindex', '-1');
          pill.classList.remove('kit-tier__size-pill--unavailable');
        } else {
          pill.setAttribute('aria-disabled', 'true');
          // Do NOT set the real disabled attribute -- see Fix 4 above.
          pill.classList.add('kit-tier__size-pill--unavailable');
        }
      });

      // If the previously-selected size is no longer available in the new color,
      // clear it so the customer is prompted to re-pick.
      if (this._collarSelectedSize && !sizeToVariant[this._collarSelectedSize]) {
        this._collarSelectedSize = null;
        pills.forEach((p) => p.setAttribute('aria-checked', 'false'));
      }

      // Re-apply the currently-selected size's variant id to the tier addons
      // so a still-valid selection survives the color change.
      if (this._collarSelectedSize && sizeToVariant[this._collarSelectedSize]) {
        this.applyCollarSizeToTier(tier, sizeToVariant[this._collarSelectedSize]);
      }
    }

    applyCollarSizeToTier(tier, collarVariantId) {
      // tier.dataset.tierAddons is "leashVariantId,collarVariantId". Replace
      // the collar variant id (last comma-separated value) with the picked
      // size's variant id. Also recompute tierLiveTotal because different
      // collar sizes have different prices.
      const ids = (tier.dataset.tierAddons || '').split(',').map((s) => s.trim()).filter(Boolean);
      if (ids.length === 0) return;
      // Collar is the LAST addon in tier 3 (leash, then collar). The dataset
      // write is synchronous so the last tap always wins for cart-add. The
      // async price refresh below is the part that needs a token guard.
      ids[ids.length - 1] = String(collarVariantId);
      tier.dataset.tierAddons = ids.join(',');

      // Token-stamp this call so two rapid pill taps where the older Promise
      // resolves second (slow CDN, race) cannot overwrite the newer tap's
      // displayed price. Without this guard the cart would carry the latest
      // variant id (correct) but the picker would show a price tied to the
      // earlier tap's variant. Mirrors the _syncToken pattern used in
      // syncAddonsToColor for the same class of race.
      this._collarSizeToken = (this._collarSizeToken || 0) + 1;
      const myToken = this._collarSizeToken;

      // Recompute price based on the actual chosen variants. Sum the addon
      // prices independently and write tier.dataset.tierAddonPrice so the
      // next onVariantChange uses the up-to-date collar variant price in
      // its addon-anchored delta formula. tierBaseTotal stays untouched.
      const harnessPrice = this.currentVariantPrice || this.initialVariantPrice;
      Promise.all(this._collarSizeRecomputeHandles(tier)).then((variants) => {
        if (myToken !== this._collarSizeToken) return;
        let addonPrice = 0;
        variants.forEach((v) => { if (v && typeof v.price === 'number') addonPrice += v.price; });
        const total = harnessPrice + addonPrice;
        const tierSavings = parseInt(tier.dataset.tierSavingsAmount, 10) || 0;
        const tierDiscounted = tierSavings > 0 ? Math.max(0, total - tierSavings) : total;
        tier.dataset.tierAddonPrice = String(addonPrice);
        tier.dataset.tierLiveTotal = String(total);
        tier.dataset.tierLiveDiscounted = String(tierDiscounted);
        const priceEl = tier.querySelector('[data-kit-price]');
        if (priceEl) priceEl.textContent = this.formatMoney(tierDiscounted);
        const wasEl = tier.querySelector('[data-kit-price-was]');
        if (wasEl) wasEl.textContent = this.formatMoney(total);
        if (tier === this.selectedTier) this.refreshSubmitLabel(false);
      });
    }

    _collarSizeRecomputeHandles(tier) {
      // Helper: for the tier's current addons dataset, fetch each variant's
      // price by walking the cached addon products. We don't always have
      // direct variant-by-id lookup so iterate the cached products.
      const ids = (tier.dataset.tierAddons || '').split(',').map(Number).filter(Number.isFinite);
      const cache = this._addonCache || {};
      const promises = [];
      ids.forEach((id) => {
        promises.push(Promise.all(Object.values(cache)).then((products) => {
          for (const p of products) {
            if (!p || !p.variants) continue;
            const found = p.variants.find((v) => v.id === id);
            if (found) return found;
          }
          return null;
        }));
      });
      return promises;
    }

    onCollarSizeClick(pill) {
      // Fix 4: guard on aria-disabled only (real disabled is no longer set on pills).
      if (pill.getAttribute('aria-disabled') === 'true') return;
      const size = pill.getAttribute('data-kit-collar-size-option');
      if (!size) return;
      const variantId = this._collarVariantsBySize[size];
      if (!variantId) return;

      this._collarSelectedSize = size;
      this.clearCollarSizeError();

      // Radiogroup state: checked + roving tabindex stay in sync for mouse and keyboard.
      const pills = pill.parentElement.querySelectorAll('[data-kit-collar-size-option]');
      pills.forEach((p) => {
        const isSel = p === pill;
        p.setAttribute('aria-checked', isSel ? 'true' : 'false');
        p.setAttribute('tabindex', isSel ? '0' : '-1');
      });

      // Apply the new collar variant id to the tier addons + recompute price.
      const tier = pill.closest('.kit-tier');
      if (tier) this.applyCollarSizeToTier(tier, variantId);
    }

    setCollarSizeError(msg) {
      const errEl = this.querySelector('[data-kit-collar-size-error]');
      const picker = this.querySelector('[data-kit-collar-size]');
      if (errEl) {
        errEl.textContent = msg || 'Pick a collar size.';
        errEl.hidden = false;
      }
      if (picker) picker.classList.add('kit-tier__collar-size--error');
    }

    clearCollarSizeError() {
      const errEl = this.querySelector('[data-kit-collar-size-error]');
      const picker = this.querySelector('[data-kit-collar-size]');
      if (errEl) {
        errEl.hidden = true;
        errEl.textContent = '';
      }
      if (picker) picker.classList.remove('kit-tier__collar-size--error');
    }

    buildShopPayBundleUrl() {
      // Returns the multi-line cart permalink the Shop Pay interceptor will navigate to
      // for the currently-selected bundle tier, or null if not applicable. Exposed as a
      // method so the URL is testable without invoking navigation.
      if (!this.selectedTier) return null;
      // Bail when the selected harness variant is unavailable. Without this guard the
      // interceptor would ship the customer to /cart/{oos_id}:1,...?payment=shop_pay
      // and Shopify's checkout shell 422s on the OOS variant, leaving the customer on
      // a "Cart cannot be loaded" page.
      if (this._lastAvailable === false) return null;
      const variantId = this.currentVariantId;
      if (!variantId) return null;
      const tier = this.selectedTier.dataset.tier;
      const addons = this.parseAddons(this.selectedTier);
      if (tier === '1' || addons.length === 0) return null;
      // Tier 3 requires an explicit collar size selection. Without one we can't
      // guarantee the right variant lands in the cart, so bail out of the Shop
      // Pay fast-path and let the customer go through the kit submit (which
      // surfaces the validation message).
      if (tier === '3' && !this._collarSelectedSize) return null;
      const parts = [String(variantId) + ':1'];
      addons.forEach((id) => parts.push(String(id) + ':1'));
      return '/cart/' + parts.join(',') + '?payment=shop_pay';
    }

    syncShopPayKit() {
      // Document-level click delegation. Previously the interceptor was
      // attached directly to the Shop Pay <button> node and re-attached on
      // every selectTier / variant change. Risk: Shopify's payment-button
      // JavaScript can re-render the inner <button> any time it hydrates
      // (slow connection, async script load, A/B test variant), detaching
      // our listener silently between our re-bind moments. Customer clicks
      // would then fall through to native single-variant Shop Pay and ship
      // just the harness to checkout instead of the full bundle.
      //
      // Delegating at the document level means we never touch the Shop Pay
      // button DOM at all. We walk from the click target up via closest()
      // to detect a Shop Pay click inside our product-info, then decide at
      // CLICK time whether to intercept based on the current tier and
      // collar-size state. The handler binds once per element lifetime and
      // tears down in disconnectedCallback.
      if (this._shopPayInterceptor) return;

      this._shopPayInterceptor = (evt) => {
        const productInfo = this.closest('product-info');
        if (!productInfo) return;
        const container = evt.target.closest('.shopify-payment-button');
        if (!container || !productInfo.contains(container)) return;
        const url = this.buildShopPayBundleUrl();
        if (!url) return;
        evt.preventDefault();
        evt.stopImmediatePropagation();
        window.location.href = url;
      };
      // Capture phase so we beat Shopify's own click handler on the button.
      document.addEventListener('click', this._shopPayInterceptor, true);
    }

    _unlockBodyScroll() {
      // Idempotent. Mirrors the position:fixed scroll-lock trick from the
      // size-guide open handler. Safe to call when no lock is held: clearing
      // already-empty styles is a DOM no-op and the conditional scrollTo
      // avoids snapping the page to 0 on a stray invocation. Reachable from
      // close, visibility/pageshow safety net, and disconnectedCallback.
      // Only acts when WE hold the lock (_lockHeld true). Without this guard
      // a third-party script applying position:fixed to body (Lucky Orange,
      // a cookie consent bar, a future Klaviyo overlay) would be misread as
      // our lock and the watchdog's scrollTo would jump the page to top.
      if (!this._lockHeld) return;
      const lockedY = this._lockedScrollY || 0;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.paddingRight = '';
      window.scrollTo(0, lockedY);
      this._lockedScrollY = 0;
      this._lockHeld = false;
    }

    disconnectedCallback() {
      if (typeof this.variantSubscription === 'function') this.variantSubscription();
      this.variantSubscription = null;
      // Detach the document-level Shop Pay click interceptor. Without this
      // the closure keeps a reference to the disconnected kit-picker,
      // preventing GC across theme-editor block toggles AND leaving a stale
      // handler that would try to mutate a detached element on click.
      if (this._shopPayInterceptor) {
        document.removeEventListener('click', this._shopPayInterceptor, true);
        this._shopPayInterceptor = null;
      }
      // Tear down size-guide global listeners + release any held scroll lock
      // so theme-editor toggles + bfcache restores never leak a stuck body.
      if (this._sizeGuideDocClick) {
        document.removeEventListener('click', this._sizeGuideDocClick);
        this._sizeGuideDocClick = null;
      }
      if (this._sizeGuideKeydown) {
        document.removeEventListener('keydown', this._sizeGuideKeydown);
        this._sizeGuideKeydown = null;
      }
      if (this._sizeGuideRecover) {
        document.removeEventListener('visibilitychange', this._sizeGuideRecover);
        window.removeEventListener('pagehide', this._sizeGuideRecover);
        window.removeEventListener('pageshow', this._sizeGuideRecover);
        this._sizeGuideRecover = null;
      }
      this._unlockBodyScroll();
      // Fix 8: remove delegated listeners stored as instance properties so
      // theme-editor section re-renders don't stack duplicate handlers.
      if (this._collarPillHandler) {
        this.removeEventListener('click', this._collarPillHandler);
        this._collarPillHandler = null;
      }
      if (this._collarRadiogroupHandler) {
        this.removeEventListener('keydown', this._collarRadiogroupHandler);
        this._collarRadiogroupHandler = null;
      }
      if (this._sizeGuideLinkHandler) {
        this.removeEventListener('click', this._sizeGuideLinkHandler, true);
        this._sizeGuideLinkHandler = null;
      }
      // Remove focus trap in case it was left registered on an open sheet.
      if (this._sizeGuide && this._sizeGuideFocusTrap) {
        this._sizeGuide.removeEventListener('keydown', this._sizeGuideFocusTrap);
      }
      clearTimeout(this._priceAnnounceTimer);
      // Drop the addon-product Promise cache.
      this._addonCache = null;
    }

    selectTier(tier, opts = {}) {
      if (tier.classList.contains('kit-tier--unavailable')) return;
      const wasSelected = tier.classList.contains('kit-tier--selected');
      this.tiers.forEach(t => t.classList.remove('kit-tier--selected'));
      tier.classList.add('kit-tier--selected');
      this.selectedTier = tier;
      this.refreshSubmitLabel(true);
      this.clearError();

      // Reset collar size state when leaving Tier 3 so the customer can't carry
      // a stale selection forward if they switch tiers.
      if (tier.dataset.tier !== '3') {
        this._collarSelectedSize = null;
        this.clearCollarSizeError();
        const allPills = this.querySelectorAll('[data-kit-collar-size-option]');
        allPills.forEach((p, i) => {
          p.setAttribute('aria-checked', 'false');
          p.setAttribute('tabindex', i === 0 ? '0' : '-1');
        });
        // Restore tier-3's addons dataset to the Liquid-rendered defaults (leash
        // + first-available collar variant) in case we'd overridden it on a
        // prior tier-3 visit with a customer-picked size.
        const tier3 = this.tiers.find((t) => t.dataset.tier === '3');
        if (tier3 && tier3._defaultTierAddons != null) {
          tier3.dataset.tierAddons = tier3._defaultTierAddons;
        }
      }

      // Mark the host product-info with the active tier (CSS hooks for any kit-mode rules)
      // and re-sync the Shop Pay interceptor so it knows the new tier's addons.
      const productInfo = this.closest('product-info');
      if (productInfo) {
        productInfo.classList.remove('kit-mode-tier-1', 'kit-mode-tier-2', 'kit-mode-tier-3');
        productInfo.classList.add(`kit-mode-tier-${tier.dataset.tier}`);
      }
      this.syncShopPayKit();

      // Pop animation on the freshly-selected tier (skip if user re-clicked the same tier).
      if (!opts.silent && !wasSelected) {
        tier.classList.remove('kit-tier--just-selected');
        void tier.offsetWidth;
        tier.classList.add('kit-tier--just-selected');
        // Filter by animationName + target so the first-render animations bubbling from
        // child elements (kit-tier-rise, kit-thumb-fade) don't strip the class early.
        // Fix 7: setTimeout fallback so prefers-reduced-motion (where animationend never
        // fires) cannot leave the class stuck and the listener permanently attached.
        let endDone = false;
        const cleanEnd = () => {
          if (endDone) return;
          endDone = true;
          tier.classList.remove('kit-tier--just-selected');
          tier.removeEventListener('animationend', onEnd);
        };
        const onEnd = (evt) => {
          if (evt.target !== tier || evt.animationName !== 'kit-tier-pop') return;
          cleanEnd();
        };
        tier.addEventListener('animationend', onEnd);
        setTimeout(cleanEnd, 400);
      }

      if (!opts.silent) {
        this.dispatchEvent(new CustomEvent('kit-picker:tier-change', {
          bubbles: true,
          detail: { tier: tier.dataset.tier, addons: this.parseAddons(tier) }
        }));
      }
    }

    parseAddons(tier) {
      const raw = tier.dataset.tierAddons || '';
      return raw.split(',').map(s => s.trim()).filter(Boolean).map(Number).filter(Number.isFinite);
    }

    onVariantChange(event) {
      const v = event && event.data && event.data.variant;
      if (!v || typeof v.price !== 'number') return;

      this.currentVariantId = v.id;

      // Update the harness thumbnail across every tier to the newly-selected variant's
      // featured image so the kit picker visually reflects the customer's color choice.
      if (v.featured_image && v.featured_image.src) {
        const harnessImgUrl = v.featured_image.src;
        this.querySelectorAll('[data-kit-thumb-role="main"] img').forEach((img) => {
          // Append width param if Shopify CDN URL; otherwise use as-is.
          img.src = harnessImgUrl.indexOf('cdn.shopify.com') >= 0
            ? harnessImgUrl.split('?')[0] + '?width=80'
            : harnessImgUrl;
        });
      }

      const newPrice = v.price;
      const priceUnchanged = (newPrice === this.currentVariantPrice);

      // Commit the new harness price BEFORE kicking the async addon sync so
      // the .then resolution (and any applyCollarSizeToTier invoked from
      // within it) reads the up-to-date harness price rather than the prior
      // value. This was a real race window on rapid size changes that mixed
      // collar-size async resolution with the new harness baseline.
      this.currentVariantPrice = newPrice;

      // Swap addon variants + thumbnails to the harness color when the addon has a
      // matching color. If no match (the addons' palette doesn't include this color)
      // we leave the addon variant + thumbnail unchanged.
      this.syncAddonsToColor(v);

      // Always run the availability + submit-disable + stock message refresh, even when
      // the variant has the same price (e.g. switching colour at the same size).
      if (priceUnchanged) {
        this.applyStockState(v);
        return;
      }

      this.tiers.forEach(tier => {
        // Pure addon-anchored formula: newTotal = newHarnessPrice + addonPrice.
        // No reference to tierBaseTotal or initialVariantPrice. tierAddonPrice
        // is Liquid-rendered initially and overwritten by syncAddonsToColor /
        // applyCollarSizeToTier whenever the addon variant changes. This
        // structure replaces the previous delta math which double-counted
        // the harness price after syncAddonsToColor wrote a post-flip value
        // back into tierBaseTotal.
        const addonPrice = parseInt(tier.dataset.tierAddonPrice, 10) || 0;
        const newTotal = newPrice + addonPrice;
        const savings = parseInt(tier.dataset.tierSavingsAmount, 10) || 0;
        const newDiscounted = savings > 0 ? Math.max(0, newTotal - savings) : newTotal;

        const priceEl = tier.querySelector('[data-kit-price]');
        if (priceEl) {
          const old = priceEl.textContent;
          const next = this.formatMoney(newDiscounted);
          if (old !== next) {
            priceEl.textContent = next;
            priceEl.classList.remove('kit-tier__price--changed');
            void priceEl.offsetWidth;
            priceEl.classList.add('kit-tier__price--changed');
            // Filter to the kit-price-flip animation specifically; other animations on the
            // same element (or bubbling from descendants) must not strip the class.
            // Fix 7: setTimeout fallback for prefers-reduced-motion, where the keyframe
            // is suppressed by the browser and animationend never fires, leaving the
            // class stuck and leaking the listener. Whichever fires first wins.
            let priceEndDone = false;
            const cleanPriceEnd = () => {
              if (priceEndDone) return;
              priceEndDone = true;
              priceEl.classList.remove('kit-tier__price--changed');
              priceEl.removeEventListener('animationend', onPriceEnd);
            };
            const onPriceEnd = (evt) => {
              if (evt.target !== priceEl || evt.animationName !== 'kit-price-flip') return;
              cleanPriceEnd();
            };
            priceEl.addEventListener('animationend', onPriceEnd);
            setTimeout(cleanPriceEnd, 400);
          }
        }

        // Update the strikethrough "was" price too, but only when this tier has a
        // savings amount configured (Tier 1 has none, no strikethrough element exists).
        const wasEl = tier.querySelector('[data-kit-price-was]');
        if (wasEl) wasEl.textContent = this.formatMoney(newTotal);

        // Update dataset so selectTier reads live totals. tierLiveTotal stays the
        // pre-discount sum (it's what the ATC label is built from for the discounted
        // total separately, and what shipping threshold compares against).
        tier.dataset.tierLiveTotal = String(newTotal);
        tier.dataset.tierLiveDiscounted = String(newDiscounted);

        // Toggle "Free shipping" pill against the DISCOUNTED total since that's
        // what the customer actually pays for the qualifying check at cart.
        const ship = tier.querySelector('[data-kit-shipfree]');
        const threshold = parseInt(this.dataset.freeShipThreshold, 10) || 3500;
        if (ship) ship.classList.toggle('kit-tier__shipfree--hidden', newDiscounted < threshold);
      });

      // Refresh the ATC label so it matches the newly-priced selected tier.
      // (currentVariantPrice was already committed above, before syncAddonsToColor.)
      if (this.selectedTier) this.refreshSubmitLabel();

      // Fix 6: announce the updated price for the selected tier to screen readers via
      // the existing [data-kit-status] role=status live region. Debounced 300ms so
      // rapid size changes don't flood the SR with incomplete announcements.
      // We reuse [data-kit-status] rather than creating a second live region.
      if (this.selectedTier && this._initialized) {
        clearTimeout(this._priceAnnounceTimer);
        this._priceAnnounceTimer = setTimeout(() => {
          const status = this.querySelector('[data-kit-status]');
          if (!status) return;
          const tierNum = this.selectedTier && this.selectedTier.dataset.tier;
          const labels = (this.config.tierLabels) || {};
          const label = labels[tierNum] || 'kit';
          const disc = parseInt(this.selectedTier && this.selectedTier.dataset.tierLiveDiscounted, 10);
          if (!Number.isNaN(disc)) {
            status.textContent = `${label}, now ${this.formatMoney(disc)}`;
          }
        }, 300);
      }

      // Variant changes can update currentVariantId; keep the Shop Pay permalink in sync.
      this.syncShopPayKit();

      // Tell the sticky CTA (and any other listeners) that the kit's display price
      // changed so they can refresh their copy. Without this the sticky bar would
      // show a stale price after a size change.
      this.dispatchEvent(new CustomEvent('kit-picker:price-change', {
        bubbles: true,
        detail: { variantId: this.currentVariantId, price: newPrice }
      }));

      // If variant is unavailable, disable submit + show stock message.
      this.applyStockState(v);
    }

    applyStockState(variant) {
      const isUnavailable = !variant || variant.available === false;
      // Track last-known availability so releaseSubmit and buildShopPayBundleUrl can
      // respect it (rather than clobbering OOS aria-disabled or shipping a 422 URL).
      this._lastAvailable = !isUnavailable;
      if (this.submitButton) {
        if (isUnavailable) this.submitButton.setAttribute('aria-disabled', 'true');
        else this.submitButton.removeAttribute('aria-disabled');
      }
      if (this.stockMsgEl) {
        if (isUnavailable) {
          // Build "Color / Size is sold out. Try another size or get notified."
          const color = variant && variant.option1 ? variant.option1 : '';
          const size = variant && variant.option2 ? variant.option2 : '';
          let msg = 'Out of stock';
          if (color || size) {
            const parts = [color, size].filter(Boolean).join(' / ');
            msg = parts + ' is out. Pick another size or color.';
          } else {
            msg = 'Out of stock. Pick another size or color.';
          }
          this.stockMsgEl.textContent = msg;
          this.stockMsgEl.hidden = false;
        } else {
          this.stockMsgEl.hidden = true;
          this.stockMsgEl.textContent = '';
        }
      }
    }

    refreshSubmitLabel(animate) {
      if (!this.selectedTier || !this.submitLabel) return;
      const tierNum = this.selectedTier.dataset.tier;
      const labels = (this.config.tierLabels) || {};
      const label = labels[tierNum] || 'kit';
      // Use the discounted total if this tier has savings configured, so the
      // CTA label matches what the customer will actually pay at cart.
      const liveDisc = parseInt(this.selectedTier.dataset.tierLiveDiscounted, 10);
      const live = parseInt(this.selectedTier.dataset.tierLiveTotal, 10);
      const base = parseInt(this.selectedTier.dataset.tierBaseTotal, 10);
      const savings = parseInt(this.selectedTier.dataset.tierSavingsAmount, 10) || 0;
      let total;
      if (!Number.isNaN(liveDisc)) {
        total = liveDisc;
      } else if (!Number.isNaN(live)) {
        total = savings > 0 ? Math.max(0, live - savings) : live;
      } else if (!Number.isNaN(base)) {
        total = savings > 0 ? Math.max(0, base - savings) : base;
      } else {
        total = NaN;
      }
      const next = !Number.isNaN(total)
        ? `Add ${label} ${this.formatMoney(total)}`
        : `Add ${label} to cart`;
      this.submitLabel.textContent = next;

      // SR announcement: only fires on user action, not initial render.
      const status = this.querySelector('[data-kit-status]');
      if (status && this._initialized) {
        status.textContent = `${label} selected, total ${!Number.isNaN(total) ? this.formatMoney(total) : ''}`;
      }
    }

    formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, this.moneyFormat);
      }
      return `$${(cents / 100).toFixed(2)}`;
    }

    onSubmit(evt) {
      if (evt && evt.preventDefault) evt.preventDefault();
      if (!this.submitButton) return;
      // Set aria-disabled FIRST to block double-submit before any async work.
      if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.inFlight) return;
      this.inFlight = true;
      this.submitButton.setAttribute('aria-disabled', 'true');

      if (!this.selectedTier) { this.releaseSubmit(); return; }
      const variantId = this.currentVariantId;
      if (!variantId) {
        this.showError('Please choose a size and color first.');
        this.releaseSubmit();
        return;
      }

      const addonIds = this.parseAddons(this.selectedTier);
      const tierNum = this.selectedTier.dataset.tier;
      // Tier 3 (Complete Bundle) requires an explicit collar size selection.
      // Block the submit and surface the inline error if no size is picked,
      // then scroll the size picker into view for keyboard / SR users.
      if (tierNum === '3' && !this._collarSelectedSize) {
        this.setCollarSizeError('Pick a collar size before adding the Complete Bundle.');
        const picker = this.querySelector('[data-kit-collar-size]');
        if (picker && typeof picker.scrollIntoView === 'function') {
          picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        this.releaseSubmit();
        return;
      }
      const tierKey = ({ '1': 'tier_1', '2': 'tier_2', '3': 'tier_3' })[tierNum] || 'kit';
      // Append random suffix to defeat same-millisecond _kit_id collisions
      // (concurrent tabs, double-click slipping past inFlight). Discount Function keys off this.
      const kitId = `kit_${this.config.mainProductId}_${tierKey}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
      const properties = (addonIds.length > 0)
        ? { _kit_id: kitId, _kit_tier: tierKey }
        : undefined;
      const items = [{ id: variantId, quantity: 1, properties }];
      addonIds.forEach(id => items.push({ id, quantity: 1, properties }));

      this.setLoading(true);

      const sectionsToRender = [
        'cart-drawer',
        'cart-icon-bubble',
        'cart-drawer-addons'
      ];

      const url = (window.routes && window.routes.cart_add_url) || '/cart/add';

      // Fix 9: helper that posts items to /cart/add.js and returns the parsed JSON.
      // Preserves the raw HTTP status so the caller can detect a 422 without
      // swallowing it inside .json(). Only called once on the happy path; a second
      // call with the harness-only item array happens exclusively on a 422 to avoid
      // adding a pre-flight check or always-two-calls overhead.
      const postItems = (itemArray) =>
        fetch(`${url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: itemArray,
            sections: sectionsToRender,
            sections_url: window.location.pathname
          })
        }).then((r) => r.json().then((data) => ({ status: r.status, data })));

      postItems(items)
        .then(({ status, data }) => {
          // Fix 9: on 422 the multi-line add failed (typically a sold-out addon
          // with DENY inventory policy making the whole batch atomic). Retry with
          // the harness line only so the in-stock product always lands in cart,
          // then surface a message naming the addon that blocked the bundle.
          if (status === 422 && addonIds.length > 0) {
            const harnessOnly = [{ id: variantId, quantity: 1, properties }];
            return postItems(harnessOnly).then(({ status: retryStatus, data: retryData }) => {
              if (retryStatus >= 400) {
                const msg = (retryData && (retryData.description || retryData.message)) || 'Could not add to cart.';
                this.showError(msg);
                this.setLoading(false);
                this.releaseSubmit();
                return;
              }
              // Harness added; let the customer know an addon was sold out.
              const addonName = (data && data.description) ? data.description : 'An add-on';
              this.showError(`${addonName} just sold out -- added the rest to your cart.`);
              return this.openCartDrawer(retryData && retryData.sections).then(() => {
                this.setSuccess();
                this.releaseSubmit();
              });
            });
          }
          if (data && data.status && data.status >= 400) {
            const msg = data.description || data.message || 'Could not add to cart.';
            this.showError(msg);
            this.setLoading(false);
            this.releaseSubmit();
            return;
          }
          return this.openCartDrawer(data && data.sections).then(() => {
            // Clear any leftover error text from a prior failed submit
            // (network blip, OOS variant) so the success state is not shown
            // alongside a stale red message that the SR would announce twice.
            this.clearError();
            this.setSuccess();
            this.releaseSubmit();
          });
        })
        .catch(() => {
          this.showError('Something went wrong. Please try again.');
          this.setLoading(false);
          this.releaseSubmit();
        });
    }

    openCartDrawer(sections) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        return Promise.resolve();
      }
      // Cart drawer was rendered with `is-empty` class on initial page load if cart was empty.
      // After our kit-add it now has items, but both the host AND the inner cart-drawer-items
      // host stay stale until renderContents replaces their innerHTML. Strip both up-front.
      cartDrawer.classList.remove('is-empty');
      const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
      if (cartDrawerItems) cartDrawerItems.classList.remove('is-empty');
      if (sections && typeof cartDrawer.renderContents === 'function') {
        try { cartDrawer.renderContents({ sections }, true); }
        catch(e) {
          if (typeof cartDrawer.reload === 'function') cartDrawer.reload();
        }
        // Some lazy templates inside the swapped section may not auto-hydrate.
        if (typeof window.loadTemplateContent === 'function') {
          try { window.loadTemplateContent(cartDrawer); } catch(e) {}
        }
        if (typeof cartDrawer.open === 'function') cartDrawer.open();
        return Promise.resolve();
      }
      if (typeof cartDrawer.reload === 'function') {
        const ret = cartDrawer.reload();
        const p = (ret && typeof ret.then === 'function') ? ret : Promise.resolve();
        return p.then(() => {
          if (typeof cartDrawer.open === 'function') cartDrawer.open();
        });
      }
      if (typeof cartDrawer.open === 'function') cartDrawer.open();
      return Promise.resolve();
    }

    setLoading(state) {
      if (!this.submitButton) return;
      const sp = this.submitButton.querySelector('.kit-picker__submit-spinner');
      const label = this.submitButton.querySelector('[data-kit-submit-label]');
      const status = this.querySelector('[data-kit-status]');
      if (state) {
        this.submitButton.classList.add('loading');
        this.submitButton.setAttribute('aria-busy', 'true');
        if (sp) sp.classList.remove('hidden');
        if (label) {
          label.dataset.prevText = label.textContent;
          label.textContent = 'Adding to cart...';
        }
        if (status) status.textContent = 'Adding to cart, please wait';
      } else {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-busy');
        if (sp) sp.classList.add('hidden');
        if (label && label.dataset.prevText) {
          label.textContent = label.dataset.prevText;
          delete label.dataset.prevText;
        }
      }
    }

    setSuccess() {
      if (!this.submitButton) return;
      const label = this.submitButton.querySelector('[data-kit-submit-label]');
      const sp = this.submitButton.querySelector('.kit-picker__submit-spinner');
      const status = this.querySelector('[data-kit-status]');
      if (sp) sp.classList.add('hidden');
      this.submitButton.classList.add('success');
      this.submitButton.classList.remove('loading');
      this.submitButton.removeAttribute('aria-busy');
      if (label) {
        label.dataset.prevText = label.dataset.prevText || label.textContent;
        label.textContent = 'Added';
      }
      if (status) status.textContent = 'Kit added to cart';
      setTimeout(() => {
        this.submitButton.classList.remove('success');
        if (label && label.dataset.prevText) {
          label.textContent = label.dataset.prevText;
          delete label.dataset.prevText;
        }
      }, 900);
    }

    releaseSubmit() {
      this.inFlight = false;
      if (!this.submitButton) return;
      // Respect last-known stock state. If the variant became OOS during the in-flight
      // request, releaseSubmit must NOT re-enable the submit. Same when the page
      // initially loaded on an unavailable variant.
      if (this._lastAvailable === false || this.dataset.initialAvailable === 'false') {
        this.submitButton.setAttribute('aria-disabled', 'true');
      } else {
        this.submitButton.removeAttribute('aria-disabled');
      }
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  }

  customElements.define('kit-picker', KitPicker);
}
